import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { requestLogin } from './UserLogin';
import { t } from '../i18n';
import './UserLogin.css';
import './LoginModal.css';

// ═══════════════════════════════════════════════════════════
// LOGIN MODAL — вхід читача без переходу на сторінку входу (гість:
// кнопка «Увійти» в панелі користувача і на картці книги). Розмітка й
// класи lp-* — ті самі, що в картці входу UserLogin.js; власні тут лише
// обгортки lm-*. Десктоп — вікно по центру, телефон (@xs) — на весь екран.
// Після успішного входу App.js перемонтовує каталог під новим читачем,
// тож вікно закривається саме (окремо закривати не треба).
// ═══════════════════════════════════════════════════════════
function LoginModal({ lang, onLogin, onRegister, onClose }) {
  const [loginVal, setLoginVal] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const loginRef = useRef(null);

  // Блокуємо скрол сторінки + Esc закриває вікно. Фокус на логін — тільки
  // з мишею: на телефоні він одразу відкрив би клавіатуру під час анімації.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    if (window.matchMedia('(pointer: fine)').matches) loginRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const submit = async e => {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError('');
    try {
      const data = await requestLogin({ login: loginVal, password });
      if (data.success) { onLogin(data); return; }
      setError(data.message || t(lang, 'login_err_generic'));
    } catch {
      setError(t(lang, 'login_err_conn'));
    }
    setLoading(false);
  };

  return createPortal(
    // onMouseDown, а не onClick — виділення тексту в полі, відпущене над
    // фоном, не закриває вікно
    <div className="lm lm-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="lm-card" role="dialog" aria-modal="true" aria-labelledby="lm-title" onSubmit={submit}>
        <button type="button" className="lm-close" onClick={onClose} aria-label={t(lang, 'sidebar_close')}>
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="lp-card-head">
          <div className="lp-card-icon">
            <i className="fa-solid fa-right-to-bracket" />
          </div>
          <div>
            <div className="lp-card-title" id="lm-title">{t(lang, 'login_title')}</div>
            <div className="lp-card-sub">{t(lang, 'login_sub')}</div>
          </div>
        </div>

        {error && (
          <div className="lp-alert" role="alert">
            <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        <div className="lp-field">
          <label className="lp-label" htmlFor="lm-login">{t(lang, 'login_login')}</label>
          <div className="lp-igroup">
            <span className="lp-ipfx"><i className="fa-solid fa-user" /></span>
            <input
              ref={loginRef}
              id="lm-login"
              className="lp-input"
              type="text"
              name="username"
              placeholder={t(lang, 'login_placeholder')}
              value={loginVal}
              onChange={e => setLoginVal(e.target.value)}
              autoComplete="username"
            />
          </div>
        </div>

        <div className="lp-field lm-field-pwd">
          <label className="lp-label" htmlFor="lm-pwd">{t(lang, 'login_pwd')}</label>
          <div className="lp-igroup">
            <span className="lp-ipfx"><i className="fa-solid fa-lock" /></span>
            <input
              id="lm-pwd"
              className="lp-input"
              type={showPwd ? 'text' : 'password'}
              name="password"
              placeholder={t(lang, 'pwd_placeholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button type="button" className="lp-eye" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
              <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          </div>
        </div>

        <button type="submit" className="lp-btn lp-btn-main" disabled={loading}>
          {loading
            ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'login_entering')}</>
            : <><i className="fa-solid fa-right-to-bracket" /> {t(lang, 'login_btn')}</>
          }
        </button>
        <div className="lp-or">
          <a className="help" href="https://lib.udu.edu.ua/posluhy/dopomoha-onlain" target="_blank" rel="nofollow noopener noreferrer">
            <span>{t(lang, 'login_forgot')}</span>
          </a>
        </div>
        <button type="button" className="lp-btn lp-btn-reg" onClick={onRegister}>
          <i className="fa-solid fa-user-plus" />
          {t(lang, 'login_register')}
        </button>
        <button type="button" className="lp-btn lm-back" onClick={onClose}>
          <i className="fa-solid fa-arrow-left" />
          {t(lang, 'back')}
        </button>
      </form>
    </div>,
    document.body
  );
}

export default LoginModal;
